import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getCachedSystemInfo } from '@/lib/runtimeMetaCache';
import { canAccessNavItem } from '@/lib/pageAccess';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Scissors,
  LayoutDashboard,
  Users,
  ShoppingBag,
  Shirt,
  Package,
  Building2,
  Factory,
  Store as StoreIcon,
  Tag,
  CheckSquare,
  MessageSquare,
  LogOut,
  User,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  PlusCircle,
  Search,
  FileText,
  Shield,
  BookOpen,
  Database,
  Settings,
  FlaskConical,
  Mail,
  Receipt,
  Warehouse,
} from 'lucide-react';

const DEFAULT_SYSTEM_INFO = {
  environment: 'live',
  db_name: '',
  is_live: true,
};

const normalizeSystemInfo = (payload = {}) => {
  const rawEnvironment = String(payload.environment || payload.app_env || '').toLowerCase().trim();
  const hasEnvironment = rawEnvironment.length > 0;
  const environmentIsLive = ['live', 'production', 'prod'].includes(rawEnvironment);

  const rawDbName = typeof payload.db_name === 'string' ? payload.db_name : '';
  const dbLooksLive = rawDbName.toLowerCase().includes('live');

  const hasBooleanLiveFlag = typeof payload.is_live === 'boolean';
  const resolvedIsLive = hasBooleanLiveFlag
    ? payload.is_live
    : (hasEnvironment ? environmentIsLive : dbLooksLive);

  const resolvedEnvironment = hasEnvironment
    ? (environmentIsLive ? 'live' : 'dev')
    : (resolvedIsLive ? 'live' : 'dev');

  return {
    ...payload,
    db_name: rawDbName,
    environment: resolvedEnvironment,
    is_live: resolvedIsLive,
  };
};

// Navigation structure with categories and role-based access
const navCategories = [
  {
    id: 'tasks',
    label: 'Tasks',
    icon: CheckSquare,
    allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'],
    items: [
      { path: '/tasks', icon: CheckSquare, label: 'Tasks', allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'] },
    ],
  },
  {
    id: 'manage',
    label: 'Manage Ord/Enq',
    icon: ShoppingBag,
    allowedRoles: ['owner', 'admin', 'manager', 'staff'],
    items: [
      { path: '/orders/dashboard', icon: LayoutDashboard, label: 'Orders Dashboard', allowedRoles: ['owner', 'admin', 'manager'] },
      { path: '/orders', icon: ShoppingBag, label: 'All Orders', allowedRoles: ['owner', 'admin', 'manager', 'staff'] },
      { path: '/orders/new', icon: PlusCircle, label: 'New Order', allowedRoles: ['owner', 'admin', 'manager', 'staff'] },
      { path: '/intercompany-costing', icon: Receipt, label: 'Intercompany Costing', allowedRoles: ['owner', 'admin'] },
    ],
  },
  {
    id: 'production',
    label: 'Production',
    icon: Shirt,
    allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'],
    items: [
      { path: '/garments', icon: Shirt, label: 'Garments', allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'] },
      { path: '/inventory', icon: Warehouse, label: 'Inventory', allowedRoles: ['owner', 'admin', 'production', 'manager'], pagePermission: 'inventory', pagePermissionRoles: ['manager'] },
      { path: '/fabric-orders', icon: Package, label: 'Fabric Procurement', allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'] },
      { path: '/google-sheet-sync', icon: Database, label: 'Google Sheet Sync (Temp)', allowedRoles: ['owner', 'admin', 'manager', 'production', 'staff'] },
      { path: '/measurement-import-priority', icon: Database, label: 'Measurement Import Priority', allowedRoles: ['owner', 'admin', 'manager', 'production', 'staff'] },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production', 'kiosk'],
    items: [
      { path: '/customers', icon: Users, label: 'All Clients', allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production'] },
      { path: '/kiosk/register', icon: PlusCircle, label: 'Kiosk / Register New Client', allowedRoles: ['owner', 'admin', 'manager', 'staff', 'production', 'kiosk'] },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Settings,
    allowedRoles: ['owner', 'admin'],
    items: [
      { path: '/products', icon: Tag, label: 'Products', allowedRoles: ['owner', 'admin'] },
      { path: '/pattern-specifications', icon: FileText, label: 'Pattern Specifications', allowedRoles: ['owner', 'admin'] },
      { path: '/stores', icon: StoreIcon, label: 'Stores', allowedRoles: ['owner', 'admin'] },
      { path: '/factories', icon: Factory, label: 'Factories', allowedRoles: ['owner', 'admin'] },
      { path: '/users', icon: User, label: 'Users', allowedRoles: ['owner', 'admin'] },
      { path: '/suppliers', icon: Building2, label: 'Suppliers', allowedRoles: ['owner', 'admin'] },
      { path: '/address-book', icon: BookOpen, label: 'Address Book', allowedRoles: ['owner', 'admin'] },
      { path: '/email-settings', icon: Mail, label: 'Email Settings', allowedRoles: ['owner', 'admin'] },
      { path: '/google-sheet-sync', icon: Database, label: 'Google Sheet Sync', allowedRoles: ['owner', 'admin'] },
      { path: '/permissions', icon: Shield, label: 'Permissions', allowedRoles: ['owner'] },
    ],
  },
];

// Helper function to check if user has access to a route
const hasAccess = (allowedRoles, userRole) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  return allowedRoles.includes(userRole);
};

const NavCategory = ({ category, isOpen, onToggle, onItemClick, currentPath, user, collapsed }) => {
  const accessibleItems = category.items.filter(item =>
    canAccessNavItem(item, user)
  );

  // Don't render category if no accessible items
  if (accessibleItems.length === 0) return null;

  // Collapsed sidebar: just show icons
  if (collapsed) {
    return (
      <div className="space-y-0.5 py-1">
        {accessibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center justify-center px-2 py-2.5 rounded-md transition-all duration-200 ${
                isActive
                  ? 'bg-orange-500/20 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`
            }
            onClick={onItemClick}
            title={item.label}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4 h-4" strokeWidth={1.5} />
          </NavLink>
        ))}
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest text-orange-400/90 hover:text-orange-300 transition-colors">
        <div className="flex items-center gap-2">
          <category.icon className="w-4 h-4" strokeWidth={1.5} />
          <span>{category.label}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-0.5 pb-2">
        {accessibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 ml-4 px-4 py-2.5 rounded-md text-[13px] font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-orange-500/20 text-white border-l-2 border-orange-500'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
              }`
            }
            onClick={onItemClick}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-4 h-4" strokeWidth={1.5} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [systemInfo, setSystemInfo] = useState(DEFAULT_SYSTEM_INFO);

  const fetchSystemInfo = useCallback(() => {
    getCachedSystemInfo()
      .then((data) => setSystemInfo(normalizeSystemInfo(data)))
      .catch(() => {});
  }, []);

  const refreshSystemInfo = useCallback(() => {
    getCachedSystemInfo({ force: true })
      .then((data) => setSystemInfo(normalizeSystemInfo(data)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchSystemInfo();

    const handleStorageUpdate = (event) => {
      if (event.key === 'global_environment_override_updated_at') {
        refreshSystemInfo();
      }
    };

    const handleOverrideChanged = () => {
      refreshSystemInfo();
    };

    window.addEventListener('storage', handleStorageUpdate);
    window.addEventListener('global-environment-override-changed', handleOverrideChanged);

    const intervalId = window.setInterval(fetchSystemInfo, 300000);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
      window.removeEventListener('global-environment-override-changed', handleOverrideChanged);
      window.clearInterval(intervalId);
    };
  }, [fetchSystemInfo]);
  
  // Track which categories are expanded - default all open including tasks
  const [openCategories, setOpenCategories] = useState({
    tasks: true,
    overview: true,
    manage: true,
    production: true,
    customers: true,
    admin: true,
  });

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - environment aware color */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen ${sidebarCollapsed ? 'w-16' : 'w-64'} ${systemInfo?.is_live ? 'bg-[#1e3a5f]' : 'bg-[#7f1d1d]'} transform transition-all duration-300 shadow-xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-6'} py-5 border-b border-white/10`}>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500 flex-shrink-0">
              <Scissors className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            {!sidebarCollapsed && (
              <>
                <div>
                  <h1 className="font-semibold text-white text-base">
                    Perfect Attire
                  </h1>
                  <p className="text-[11px] text-white/40">Bespoke Management</p>
                </div>
                <span
                  className={`ml-auto flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${systemInfo?.is_live ? 'bg-sky-400/25 border-sky-300/40 text-sky-100' : 'bg-red-400/25 border-red-300/40 text-red-200'}`}
                  data-testid="sidebar-environment-badge"
                >
                  <FlaskConical className="w-3 h-3" />
                  {systemInfo?.is_live ? 'LIVE' : 'DEV'}
                </span>
                <button
                  className="lg:hidden ml-auto text-white/60 hover:text-white transition-colors"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className={`space-y-1 ${sidebarCollapsed ? 'px-1' : 'px-3'}`}>
              {navCategories
                .filter(category => hasAccess(category.allowedRoles, user?.role || 'staff'))
                .map((category) => (
                  <NavCategory
                    key={category.id}
                    category={category}
                    isOpen={openCategories[category.id]}
                    onToggle={() => toggleCategory(category.id)}
                    onItemClick={() => setSidebarOpen(false)}
                    currentPath={location.pathname}
                    user={user}
                    collapsed={sidebarCollapsed}
                  />
                ))}
            </nav>
          </ScrollArea>

          {/* Collapse toggle */}
          <div className="hidden lg:flex justify-center py-2 border-t border-white/10">
            <button
              onClick={() => setSidebarCollapsed(prev => !prev)}
              className="p-2 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              data-testid="sidebar-collapse-toggle"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* User menu */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} w-full p-3 rounded-md hover:bg-white/10 transition-colors`}
                  data-testid="user-menu-trigger"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white font-semibold text-sm flex-shrink-0">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-white truncate">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-xs text-white/40 truncate">
                          {user?.email}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-white/40" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-muted-foreground cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-14 flex items-center px-6 gap-4 bg-card border-b border-border shadow-sm">
          <button
            className="lg:hidden text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
